/*
 * ******************************************************************
 * Copyright (c) 2022-2024 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Component, EventEmitter, Input, Output } from "@angular/core";
import { ClrDatagridFilter, ClrDatagridFilterInterface } from "@clr/angular";
import { Observable, Subject } from "rxjs";
import { L10nService } from "@vmw/ngx-vip";

export class UndefinedItem {
    constructor(public labelResourceId: string,
                public valueResourceId: string) {
    }
}

export class EnumItemModel {
    constructor(public label: string,
                public value: string,
                public selected: boolean = false) {
    }
}

// @formatter:off
@Component({
    selector: "dg-enum-filter",
    templateUrl: "./datagrid-enum-filter.component.html",
    styleUrls: ["./datagrid-enum-filter.component.scss"]
})
// @formatter:on
export class DatagridEnumFilter<T> implements ClrDatagridFilterInterface<T> {
    /**
     * Name of the property used for filtering.
     */
    @Input()
    public propertyName: string;

    @Input()
    public filterProperty: string;

    @Input()
    public set undefinedItem(item: UndefinedItem) {
        this._undefinedItem = item;
        if (item) {
            this.initFilter();
        }
    }

    public get undefinedItem(): UndefinedItem {
        return this._undefinedItem;
    }

    /**
     * Resource strings for enum values used to show in the filter.
     * @param value - array of strings
     */
    @Input()
    public set enumStrings(value: string[]) {
        if (value && value.length > 0) {
            this._enumStrings = value;
            this.initFilter();
        }
    }

    public get enumStrings(): string[] {
        return this._enumStrings;
    }

    /**
     * Localized strings for enum values used to show in the filter.
     * @param value - array of strings
     */
    @Input()
    public set localizedEnumStrings(value: string[]) {
        if (value && value.length > 0) {
            this._localizedStrings = value;
            this.initFilter();
        }
    }

    public get localizedEnumStrings(): string[] {
        return this._localizedStrings;
    }

    /**
     * Preset filter selection.
     * @param value - array of strings
     */
    @Input()
    public set clrFilterValue(value: string[]) {
        this._clrFilterValue = value;
        this.presetFilter();
    }
    public get clrFilterValue(): string[] {
        return this.selectedItems.map(item => item.value);
    }

    @Output()
    clrFilterValueChange: EventEmitter<string[]> = new EventEmitter();

    protected items: Array<EnumItemModel>;
    protected selectedItems: Array<EnumItemModel> = new Array<EnumItemModel>();
    protected buttonText: string = "filter.selectAll.button.text";
    protected allSelected: boolean = false;
    private _enumStrings: string[];
    private _localizedStrings: string[];
    private _clrFilterValue: string[];
    private _undefinedItem: UndefinedItem;
    private changesSubject: Subject<T> = new Subject<T>();

    constructor(protected filterContainer: ClrDatagridFilter,
                protected l10nService: L10nService) {
        filterContainer.setFilter(this);
    }

    /**
     * Indicates if the filter is currently active, (at least one input is set)
     */
    public isActive(): boolean {
        return this.selectedItems.length > 0;
    }

    /**
     * Tests if an item matches a search text
     */
    public accepts(item: T): boolean {
        const property = this.getProperty(item);
        for (const selected of this.selectedItems) {
            if (selected.value === property) {
                return true;
            }
        }
        return false;
    }

    public get state(): any {
        const currentState = {
            property: this.filterProperty ? this.filterProperty : this.propertyName,
            value: this.selectedItems
        }
        return currentState;
    }

    /**
     * The Observable required as part of the Filter interface
     */
    public get changes(): Observable<T> {
        return this.changesSubject.asObservable();
    }

    public onCheckboxChecked(): void {
        this.selectedItems = this.items.filter(item => item.selected === true);
        this.checkSelectedItems();
        this.clrFilterValueChange.emit(this.selectedItems.map(item => item.value));
        this.changesSubject.next(null);
    }

    checkSelectedItems(): void {
        let allSelected: boolean = true;
        for (const item of this.items) {
            if (item.value !== "EMPTY") {
                allSelected = allSelected && item.selected;
            }
        }
        this.allSelected = allSelected;
        this.updateButtonText();
    }

    updateButtonText(): void {
        this.buttonText = this.allSelected ?
            "filter.unselectAll.button.text" :
            "filter.selectAll.button.text";
    }

    onButtonClick(): void {
        this.allSelected = !this.allSelected;

        this.items.forEach(item => item.selected = this.allSelected);
        this.selectedItems = this.items.filter(item => item.selected === true);

        this.updateButtonText();
        this.clrFilterValueChange.emit(this.selectedItems.map(item => item.value));
        this.changesSubject.next(null);
    }

    private presetFilter(): void {
        if (!this._clrFilterValue) {
            return;
        }
        if (this.items) {
            for (const item of this.items) {
                if (this._clrFilterValue.includes(item.value)) {
                    item.selected = true;
                    if (!this.selectedItems.find(elem => elem.value === item.value)) {
                        this.selectedItems.push(item);
                    }
                }
            }
        }
        this.changesSubject.next(null);
    }

    private getProperty(item: T): string {
        const props: string[] = this.propertyName.split(".");
        let result: any = item;
        for (const prop of props) {
            result = result[prop];
        }
        return result;
    }

    private initFilter(): void {
        this.items = new Array<EnumItemModel>();
        if (this.enumStrings) {
            for (const item of this.enumStrings) {
                const str: string = item === "EMPTY" ? "" : this.l10nService.getMessage(item);
                this.items.push(new EnumItemModel(str, item));
            }
            if (this.undefinedItem) {
                this.items.push(new EnumItemModel(
                    this.l10nService.getMessage(this.undefinedItem.labelResourceId),
                    this.undefinedItem.valueResourceId));
            }
        } else if (this.localizedEnumStrings) {
            for (const item of this.localizedEnumStrings) {
                this.items.push(new EnumItemModel(item, item));
            }
            if (this.undefinedItem) {
                this.items.push(new EnumItemModel(
                    this.l10nService.getMessage(this.undefinedItem.labelResourceId),
                    this.l10nService.getMessage(this.undefinedItem.valueResourceId)));
            }
        }

        this.presetFilter();
    }
}
